import { useMemo } from 'react'
import SEO from '../../components/SEO'

// Shared shell for the legal pages.
//
// These are reference documents, not marketing sections: someone arrives to
// check one clause, not to read top to bottom. So the layout gives them the
// two things that makes possible -- a numbered contents list that jumps, and
// stable per-clause anchors that can be linked and cited.
export default function LegalLayout({ title, intro, path, schema, sections }) {
  const toc = useMemo(
    () => sections.map((s, i) => ({ n: i + 1, id: s.id, heading: s.heading })),
    [sections],
  )

  return (
    <div className="pb-20">
      <SEO title={`${title} - MySchoolPortal`} description={intro} path={path} schema={schema} />

      <section className="px-4 pt-16 pb-8 bg-grid-pattern">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-display font-extrabold text-ink leading-[1.1] mb-4">{title}</h1>
          <p className="text-lg text-[var(--ink-soft)] leading-relaxed max-w-2xl">{intro}</p>
        </div>
      </section>

      <section className="px-4">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-[232px_1fr] lg:gap-14">
          {/* Contents. Sticky beside the text on wide screens so the reader can
              see where a clause sits while reading it; above the text and in
              normal flow on narrow ones, where there is no room for a column
              and a sticky block would just eat the viewport. */}
          <nav aria-label="Contents" className="lg:sticky lg:top-24 lg:self-start mb-10 lg:mb-0">
            <p className="font-boxy text-[10.5px] uppercase tracking-widest text-[var(--ink-soft)] mb-4">Contents</p>
            <ol className="grid sm:grid-cols-2 lg:grid-cols-1 gap-x-8 gap-y-2 lg:max-h-[calc(100vh-10rem)] lg:overflow-y-auto lg:pr-2">
              {toc.map(({ n, id, heading }) => (
                <li key={id} className="flex gap-2.5 text-sm leading-snug">
                  <span className="font-boxy text-[var(--ink-soft)] flex-shrink-0">{n}.</span>
                  <a href={`#${id}`} className="text-[var(--ink-soft)] hover:text-accent transition-colors duration-150">
                    {heading}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="legal-prose max-w-2xl min-w-0">
            {sections.map((s, i) => (
              <section key={s.id} id={s.id} className="scroll-mt-24 mb-10">
                <h2 className="text-lg font-display font-bold text-ink mb-3 flex gap-3">
                  <span className="font-boxy text-accent flex-shrink-0">{i + 1}.</span>
                  <span>{s.heading}</span>
                </h2>
                <div className="sm:pl-8">{s.body}</div>
              </section>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
